import React from "react";
import classnames from "classnames";
import { HtmlEditor, Inject, RichTextEditorComponent } from "@syncfusion/ej2-react-richtexteditor";
import { ReactComponent as PencilIcon } from "../../../assets/icons/pencil.svg";
import { ReactComponent as BinIcon } from "../../../assets/icons/bin.svg";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AvatarGroup, Avatar } from "@mui/material";
import IconButton from "@mui/material/IconButton";

import "./NotesItem.styles.scss";
import { useFormatDateHook, useNavigate } from "hooks";
import { Menu } from "components/popups";
import { TooltipDiv } from "components";
import { INote } from "store/types";

import { useDealActions, useDealIdSelector, useUserIdSelector } from "store";

interface INotesItem {
  note: INote;
  onClickDelete: () => void;
  onClickEditNote: () => void;
}

export const NotesItem: React.FC<INotesItem> = ({ note, onClickDelete, onClickEditNote }): JSX.Element => {
  const {
    text,
    created_at,
    title,
    location,
    is_private,
    shared_with,
    // id,
    user_id
  } = note;
  const formatDate = useFormatDateHook();
  const navigate = useNavigate();
  const dealId = useDealIdSelector();
  const { updateNoteAction } = useDealActions();
  // const capitalSeekers = useDealCSSelector();
  // const capitalSeeker: ICapitalSeeker | undefined = capitalSeekers?.[0];

  const [showMore, setShowMore] = React.useState<boolean>(false);
  // const { setShareDialog } = useViewActions();
  const userId = useUserIdSelector();

  const showMoreRenderHandler = (text: string) => {
    if (!text) return false;

    const paragraphsNumber = text.match(/<p>/g)?.length || 0;
    const listNumber = text.match(/<li>/g)?.length || 0;
    if (text.length > 200 || paragraphsNumber > 3 || listNumber > 3) {
      return true;
    }
    return false;
  };

  return (
    <div className={classnames("notes-item", { "notes-item__more ": showMore })}>
      <div className="notes-item__header">
        <h1
          className={classnames("notes-item__title", {
            "notes-item__title-light": !title
          })}
        >
          {title ? title : "Insert title"}
        </h1>
        <div className="notes-item__privacy">
          {!is_private && (
            <div className=" notes-item__privacy-avatars">
              {shared_with && (
                <AvatarGroup
                  sx={{
                    "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 },
                    marginRight: "10px"
                  }}
                  max={10}
                >
                  {shared_with?.map((share, index) => (
                    <TooltipDiv key={share.id + index} place="bottom" tooltipMessage={share.full_name}>
                      <Avatar src={share.profile_photo_url} />
                    </TooltipDiv>
                  ))}
                </AvatarGroup>
              )}
              {/* <TooltipDiv tooltipMessage={"Share note"} place="top">
                <IconButton
                  sx={{ padding: "8px", marginRight: "3px" }}
                  onClick={() => {
                    if (!dealId) return;
                    setShareDialog({
                      elements: [id],
                      type: "note_id",
                      shareName: title || "Note",
                      dealId: dealId,
                      dealCapitalSeekerEmail: capitalSeeker?.email
                    });
                  }}
                >
                  <PersonAddOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                </IconButton>
              </TooltipDiv> */}
            </div>
          )}
          {user_id === userId && (
            <TooltipDiv tooltipMessage={is_private ? "Private note" : "Note shared with team"} place="top">
              <IconButton sx={{ padding: "0" }}>
                <Menu
                  icon={
                    is_private ? (
                      <LockOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                    ) : (
                      <PublicOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                    )
                  }
                  options={[
                    {
                      title: "Only me",
                      icon: <LockOutlinedIcon />,
                      onClick: () => {
                        if (is_private) return;
                        updateNoteAction({ id: note.id, is_private: true });
                      }
                    },
                    {
                      title: "Share",
                      icon: <PublicOutlinedIcon />,
                      onClick: () => {
                        if (!is_private) return;
                        updateNoteAction({ id: note.id, is_private: false });
                      }
                    }
                  ]}
                />
              </IconButton>
            </TooltipDiv>
          )}
        </div>
      </div>

      <RichTextEditorComponent className="notes-item__message" readonly={true} value={text}>
        <Inject services={[HtmlEditor]} />
      </RichTextEditorComponent>
      <div className="notes-item__footer">
        <div className=" notes-item__footer-date">
          <span className="notes-item__date">{formatDate({ date: created_at, format: "HH:mm, MMM DD, YYYY" })}</span>
          {location[1] && <div className="notes-item__divider" />}
          {location?.map((item, idx) => {
            if (!idx) {
              return;
            }
            return (
              <span
                className="notes-item__location "
                onClick={() => {
                  if (!dealId) return;
                  navigate({
                    route: item.type === "section" ? "dealFolder" : "dealForm",
                    params: { deal_id: dealId, form_id: item.id, folder_id: item.id }
                  });
                }}
                key={idx}
              >
                {item.title}
                {idx !== location.length - 1 ? <KeyboardArrowRightIcon /> : ""}
              </span>
            );
          })}
        </div>
        <div className="notes-item__footer-btn">
          {showMoreRenderHandler(text) && (
            <span className="notes-item__more-label scale-click" onClick={() => setShowMore(!showMore)}>
              {showMore ? "Show less" : "Show more"}
            </span>
          )}
          {user_id === userId && (
            <>
              <IconButton onClick={onClickDelete}>
                <BinIcon className="notes-item__delete-button scale-click" height="20px" width="20px" />
              </IconButton>
              <div className="notes-item__divider-btn" />
            </>
          )}
          <IconButton onClick={onClickEditNote}>
            <PencilIcon className="notes-item__edit-button scale-click" height="20px" width="20px" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
